/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { Badge, Stack, Table, TableProps } from '@mantine/core'
import CalendarEventHoverCard from './CalendarEventHoverCard'

export function filterDayEvents (events: CalendarEvent[], dateTime: DateTime) {
  return events.filter((event) => DateTime.fromISO(event.date)
    .hasSame(dateTime, 'day'))
}

export function getWeeksDaysAndEvents (dateTime: DateTime, events: CalendarEvent[]) {
  const minDateTime = dateTime.startOf('month').startOf('week')
  const maxDateTime = minDateTime.plus({ weeks: 5 }).endOf('week')
  const weeks = []
  let day = minDateTime

  while (day <= maxDateTime) {
    const { weekNumber } = day
    const days = []

    for (let i = 0; i < 7; i += 1) {
      const data = {
        dateTime: day,
        events: filterDayEvents(events, day)
      }
      days.push(data)
      day = day.plus({ day: 1 })
    }
    weeks.push({ days, number: weekNumber })
  }
  return weeks
}

export type AgendaProps = TableProps & {
  dateTime: DateTime,
  events: CalendarEvent[],
}

function Agenda (props: AgendaProps) {
  const {
    dateTime,
    events,
    ...others
  } = props

  const weeks = useMemo(
    () => getWeeksDaysAndEvents(dateTime, events),
    [dateTime, events])

  const cellHeight = useMemo(() => 100 / (6 + 1), [])
  const cellWidth = useMemo(() => 100 / (7 + 1), [])

  const today = useMemo(() => (
    DateTime.now()
      .setLocale(dateTime.locale)
      .setZone(dateTime.zone)
      .startOf('day')
  ), [dateTime.locale, dateTime.zone])

  return (
    <Table {...others}>
      <Table.Thead h={`${cellHeight}%`}>
        <Table.Tr>
          <Table.Td
            colSpan={7}
            fw="bold"
            tt="uppercase"
          >
            {dateTime.monthLong}
            &nbsp;
            {dateTime.year}
          </Table.Td>
        </Table.Tr>
        <Table.Tr>
          <Table.Td />
          {weeks[0].days.map((day) => (
            <Table.Td
              key={day.dateTime.weekdayShort}
              w={`${cellWidth}%`}
              fz="sm"
              c="dimmed"
              tt="capitalize"
            >
              {day.dateTime.weekdayShort}
            </Table.Td>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {weeks.map((week) => (
          <Table.Tr
            key={week.number}
            style={{ height: `${cellHeight}%` }}
          >
            <Table.Td
              w={`${cellWidth}%`}
              fz="xs"
              c="dimmed"
            >
              {week.number}
            </Table.Td>

            {week.days.map((day) => (
              <Table.Td
                key={day.dateTime.day}
                w={`${cellWidth}%`}
                valign="top"
              >
                <Badge
                  circle
                  color="dark"
                  size="lg"
                  opacity={day.dateTime.hasSame(dateTime, 'month') ? 1 : 0.5}
                  variant={day.dateTime.hasSame(today, 'day') ? 'filled' : 'transparent'}
                >
                  {day.dateTime.day}
                </Badge>

                <Stack
                  maw={100}
                  gap="xs"
                  opacity={day.dateTime < today ? 0.5 : null}
                  style={{ overflow: 'hidden' }}
                >
                  {day.events.map((event) => (
                    <CalendarEventHoverCard
                      key={event.label}
                      event={event}
                    >
                      <Badge
                        color={event.color}
                        size="sm"
                        autoContrast
                        fullWidth
                      >
                        {event.label}
                      </Badge>
                    </CalendarEventHoverCard>
                  ))}
                </Stack>
              </Table.Td>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

export default Agenda
